import { Fragment } from 'react'

import { ActionIcon, Divider, Text } from '@mantine/core'
import { X } from 'lucide-react'

import { Link, routes } from '@redwoodjs/router'

import DnDrawer from 'src/components/Core/DnDrawer'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import { formatDateWithTime } from 'src/lib/formatters'

import type { ContactAuditField, ContactHistoryEntry } from '../carrier.types'
import { contactLabel } from '../carrier.utils'
import { describeChange } from '../contactHistory.utils'

export interface CarrierContactHistoryDrawerProps {
  /** Null closes it; the entry is held by the caller so the row stays the source. */
  entry: ContactHistoryEntry | null
  onClose: () => void
}

const CHANGE_TYPE_LABEL: Record<string, string> = {
  CREATE: 'Added',
  UPDATE: 'Changed',
  DELETE: 'Removed',
}

const CHANGE_TYPE_COLOR: Record<string, 'green' | 'blue' | 'red' | 'gray'> = {
  CREATE: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
}

/**
 * What a contact arrives holding, in the order worth reading.
 *
 * A creation records every column, so the fields that identify the contact sit
 * among defaults nobody chose — a source of MANUAL, a count of 1. These are
 * listed first, which is the whole of the ordering; nothing is hidden.
 */
const IDENTIFYING_FIELDS: ContactAuditField[] = [
  'CONTACT_NAME',
  'EMAIL',
  'PHONE_NUMBER',
  'DOT_NUMBER',
]

const blank = <span className="italic text-doubleNickel-gray-400">nothing</span>

/**
 * One history entry, in full.
 *
 * The table has to fit a change, a reason and an actor across a row, so a long
 * note or a change that touched several fields reads as a fragment there. This
 * is the same entry with room for all of it — nothing is fetched, the row the
 * reader clicked is the whole source.
 */
const CarrierContactHistoryDrawer = ({
  entry,
  onClose,
}: CarrierContactHistoryDrawerProps) => {
  const described = (entry?.changes ?? []).map((change) => ({
    field: change.field,
    ...describeChange(change),
  }))

  // Keyed off the values rather than the change type: an update where every
  // field was previously empty reads like a creation and wants the same shape.
  const showPrevious = described.some((change) => change.previousValue)

  // A creation records the carrier the contact was filed under, which is the
  // carrier whose page this is. Nothing to learn. A carrier that CHANGED is
  // worth seeing, so it is only dropped when there is no previous value.
  //
  // Never dropped down to nothing, though: a contact attached to its first
  // carrier holds that one field and no other, and removing it would leave
  // the drawer reporting a save that moved nothing while the table beside it
  // names the assignment.
  const withoutCarrier = described.filter(
    (change) => change.field !== 'CARRIER_ID'
  )
  const relevant =
    showPrevious || withoutCarrier.length === 0 ? described : withoutCarrier

  const ordered = showPrevious
    ? relevant
    : [...relevant].sort(
        (a, b) =>
          (IDENTIFYING_FIELDS.indexOf(a.field) + 1 || 99) -
          (IDENTIFYING_FIELDS.indexOf(b.field) + 1 || 99)
      )

  // "updated" is true of every UPDATE and says nothing. Archiving is the one
  // the reader is usually here for, so it is named.
  const verb = (() => {
    if (entry?.changeType === 'CREATE') return 'added'
    if (entry?.changeType === 'DELETE') return 'removed'
    const status = described.find((change) => change.field === 'STATUS')
    if (status?.newValue === 'Archived') return 'archived'
    return 'updated'
  })()

  /**
   * A carrier id reads as a UUID and a DOT number reads as a dead number, and
   * both name a carrier that has a page. Rendered as real anchors — Link emits
   * an href — so they can be opened in a new tab rather than only clicked.
   */
  const asLink = (field: ContactAuditField, value: string | null) => {
    if (!value) return blank

    if (field === 'CARRIER_ID') {
      return (
        <Link
          to={routes.adminVoeCarrier({ carrierId: value })}
          className="text-doubleNickel-brand-600 hover:underline"
        >
          {value}
        </Link>
      )
    }

    if (field === 'DOT_NUMBER' && entry?.contact?.carrierId) {
      return (
        <Link
          to={routes.adminVoeCarrier({ carrierId: entry.contact.carrierId })}
          className="text-doubleNickel-brand-600 hover:underline"
        >
          {value}
        </Link>
      )
    }

    return value
  }

  return (
    <DnDrawer
      opened={Boolean(entry)}
      onClose={onClose}
      stackId="carrier-contact-history-drawer"
    >
      {entry && (
        <div className="flex h-screen flex-col space-y-4">
          {/* HEADER */}
          <div className="flex flex-row items-center justify-between gap-2">
            <div className="flex min-w-0 flex-row items-center gap-2">
              <Text size="lg" fw={500} className="truncate">
                {contactLabel(entry.contact)}
              </Text>
              <StyledBadge
                color={CHANGE_TYPE_COLOR[entry.changeType] ?? 'gray'}
              >
                {CHANGE_TYPE_LABEL[entry.changeType] ?? entry.changeType}
              </StyledBadge>
            </div>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={onClose}
              className="flex-shrink-0"
            >
              <X size={16} />
            </ActionIcon>
          </div>

          {/* BODY */}
          <div className="flex min-h-0 flex-1 flex-col space-y-4 overflow-auto pb-6">
            {/* Who, what and when as one line: three labelled rows to say
                "Gaston archived this contact" is ceremony around a sentence. */}
            <Text size="sm" fw={300} className="leading-relaxed">
              {entry.actorName ? (
                <>
                  <span className="font-medium text-doubleNickel-gray-900">
                    {entry.actorName}
                  </span>{' '}
                  {verb} this contact
                </>
              ) : (
                <span className="capitalize">{verb} automatically</span>
              )}
              <span className="text-doubleNickel-gray-500">
                {' '}
                · {formatDateWithTime(entry.createdAt)}
              </span>
            </Text>

            {/* Unlabelled: a quote block already reads as a reason, and the
                sentence above is what it is a reason for. */}
            {entry.note && (
              <Text
                size="sm"
                fw={300}
                className="whitespace-pre-wrap break-words rounded-r-md border-l-2 border-doubleNickel-gray-300 bg-doubleNickel-gray-25 px-3 py-2 leading-relaxed"
              >
                {entry.note}
              </Text>
            )}

            <Divider variant="dashed" />

            {ordered.length === 0 ? (
              // The audit trail records an update whether or not it moved
              // anything, so an entry with nothing in it is a real outcome — a
              // save that wrote the values already on file.
              <Text size="xs" fw={300} className="italic">
                Nothing changed — the save wrote the values already on file
              </Text>
            ) : (
              <>
                {/* A single change needs no heading; the sentence above has
                    already said what happened. */}
                {ordered.length > 1 && (
                  <Text
                    size="xs"
                    fw={400}
                    className="text-doubleNickel-gray-500"
                  >
                    {showPrevious ? 'WHAT CHANGED' : 'CREATED WITH'}
                  </Text>
                )}

                {/* One left edge for every value, with the before and after
                    flowing inline the way the table writes them. A column per
                    side instead leaves the two halves fighting for width, and
                    long values wrap past an arrow pinned to the first line. */}
                <div className="grid grid-cols-[96px_1fr] items-baseline gap-x-4 gap-y-3.5">
                  {ordered.map((change) => (
                    <Fragment key={change.field}>
                      <Text
                        size="xs"
                        fw={300}
                        className="text-doubleNickel-gray-500"
                      >
                        {change.label}
                      </Text>
                      <Text
                        size="xs"
                        fw={300}
                        className="break-words leading-relaxed"
                      >
                        {showPrevious && (
                          <>
                            <span className="text-doubleNickel-gray-400 line-through">
                              {change.previousValue || blank}
                            </span>{' '}
                            <span className="text-doubleNickel-gray-400">
                              →
                            </span>{' '}
                          </>
                        )}
                        <span>{asLink(change.field, change.newValue)}</span>
                      </Text>
                    </Fragment>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </DnDrawer>
  )
}

export default CarrierContactHistoryDrawer
